import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo, { siteUrl } from "../components/Seo";
import { domainOf, outboundHref, slugify } from "../lib/utils-tv";
import SponsoredSlots from "../components/SponsoredSlots";
import { API, recordLaunch } from "../lib/api";

// FAQPage for strategy/use-case hubs — questions built dynamically from the
// hub's own top-ranked tools; answers use ONLY real DB fields (best_for /
// key_feature / description / pricing), never invented. MIRRORS
// scripts/prerender/renderBody.js::strategyFaqLd — keep both in sync so the
// pre-rendered HTML and the hydrated DOM agree.
function strategyFaqLd(value, tools) {
  if (!value || !tools || !tools.length) return null;
  const v = String(value).toLowerCase();
  const top = tools[0];
  const entries = [];
  const topDesc = top.best_for || top.key_feature || top.description;
  if (topDesc) {
    entries.push({ q: `What is the best tool for ${v}?`, a: `${top.title} — ${topDesc}`.slice(0, 320) });
  }
  const free = tools.find((t) => /free/i.test(String(t.pricing || "")));
  if (free) {
    entries.push({ q: `Is there a free ${v} tool?`, a: `Yes — ${free.title} (${free.pricing}).` });
  }
  const names = tools.slice(0, 3).map((t) => t.title).filter(Boolean);
  if (names.length >= 2) {
    entries.push({
      q: `What are the top ${v} tools?`,
      a: `${names.join(", ")} are among the most-upvoted ${v} tools in the TerminalValue.io index.`,
    });
  }
  if (!entries.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: entries.map((e) => ({
      "@type": "Question",
      name: e.q,
      acceptedAnswer: { "@type": "Answer", text: e.a },
    })),
  };
}

// Mirrors scripts/prerender/renderBody.js::softwareApp so the hydrated
// client-side JSON-LD is identical to the pre-rendered HTML. Offer follows the
// no-hallucination rule: price "0" only for free tiers, never a fabricated
// number for paid tiers — just a category label.
function softwareAppLd(t) {
  const price = String(t.pricing || "").toUpperCase();
  const offers = {
    "@type": "Offer",
    priceCurrency: "USD",
    category:
      price === "FREE"
        ? "Free"
        : price.includes("CALL")
        ? "Contact for pricing"
        : "Paid",
  };
  if (price === "FREE" || price.startsWith("FREE")) offers.price = "0";
  const node = {
    "@type": "SoftwareApplication",
    name: t.title,
    url: siteUrl(`/tool/${t.slug}`),
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web",
    offers,
  };
  const desc = t.key_feature || t.description;
  if (desc) node.description = desc;
  return node;
}

// Programmatic "Best <X> tools" hub page (/best/:slug). Ranks the index's
// approved tools for a facet (category / asset class / tag) purely by upvotes —
// no hallucination, just the live ranking. Thin hubs (< min_tools) render
// noindex so we never push weak pages into Google.
export default function Best() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound | error

  useEffect(() => {
    let alive = true;
    Promise.resolve().then(() => {
      if (!alive) return;
      setStatus("loading");
      axios
        .get(`${API}/best/${encodeURIComponent(slug)}`)
        .then((r) => {
          if (!alive) return;
          setData(r.data);
          setStatus("ok");
        })
        .catch((err) => {
          if (!alive) return;
          setStatus(err?.response?.status === 404 ? "notfound" : "error");
        });
    });
    return () => {
      alive = false;
    };
  }, [slug]);

  if (status === "loading") {
    return (
      <Shell testid="best-loading" path={`/best/${slug}`} noindex title="Loading…">
        <div className="text-[13px] text-[#888]">
          loading list...<span className="tv-blink">_</span>
        </div>
      </Shell>
    );
  }

  if (status === "notfound") {
    return (
      <Shell testid="best-404" path={`/best/${slug}`} noindex title="Not found">
        <h1 className="text-xl font-bold border-b border-black pb-2">
          {">"}_ 404 / no such list
        </h1>
        <p className="mt-4 text-[13px] text-[#444]">That list isn&apos;t in the index.</p>
        <p className="mt-4 flex gap-2">
          <Link to="/best" className="tv-btn">[all lists]</Link>
          <Link to="/" className="tv-btn">[← back to index]</Link>
        </p>
      </Shell>
    );
  }

  if (status === "error" || !data) {
    return (
      <Shell testid="best-error" path={`/best/${slug}`} noindex title="Error">
        <h1 className="text-xl font-bold">{">"}_ error</h1>
        <p className="text-[13px] text-[#444] mt-2">could not load. try again.</p>
      </Shell>
    );
  }

  const tools = data.tools || [];
  const related = data.related || [];
  const path = `/best/${data.slug}`;
  // Backend decides indexability (substantial + non-duplicate). Fall back to a
  // simple thin check for older payloads without the flag.
  const thin =
    data.indexable === false ||
    (data.indexable === undefined && data.count < (data.min_tools || 3));
  const seoTitle = data.title;
  const seoDesc = data.description;

  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: data.title,
    numberOfItems: tools.length,
    itemListElement: tools.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: softwareAppLd(t),
    })),
  };

  // Strategy/use-case hubs get an extra FAQPage built from their top tools.
  const faqLd = data.kind === "strategy" ? strategyFaqLd(data.value, tools) : null;
  const ld = faqLd ? [itemListLd, faqLd] : itemListLd;

  return (
    <Shell
      testid="best-page"
      path={path}
      title={seoTitle}
      description={seoDesc}
      noindex={thin}
      ogType="article"
      jsonLd={ld}
    >
      {/* Breadcrumb */}
      <div className="text-[12px] text-[#888] mb-3" data-testid="best-breadcrumbs">
        <Link to="/" className="tv-link">index</Link>{" / "}
        <Link to="/best" className="tv-link">best</Link>{" / "}
        <span className="text-black">{data.label}</span>
      </div>

      <h1 className="text-xl font-bold border-b border-black pb-2" data-testid="best-title">
        {">"}_ best {data.value} tools
      </h1>

      <p className="mt-4 text-[14px] text-[#222] leading-relaxed" data-testid="best-intro">
        {tools.length > 0 ? (
          <>
            The {tools.length} top {data.value.toLowerCase()}{" "}
            {tools.length === 1 ? "tool" : "tools"} in the index, ranked by
            upvotes.
          </>
        ) : (
          <>No {data.value.toLowerCase()} tools in the index yet.</>
        )}
      </p>

      <SponsoredSlots placement={`best:${slug}`} />

      {/* Ranked list */}
      {tools.length > 0 ? (
        <ol className="list-none p-0 m-0 mt-6 space-y-3" data-testid="best-list">
          {tools.map((t, i) => (
            <li key={t.id} className="border-b border-[#eee] pb-3 flex gap-3">
              <span className="text-[15px] font-bold text-[#888] tabular-nums shrink-0 w-8 pt-0.5">
                {String(i + 1).padStart(2, "0")}.
              </span>
              <div className="flex-1 min-w-0">
                <BestRow tool={t} />
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <p className="mt-6 text-[13px] text-[#666]" data-testid="best-empty">
          Nothing here yet. Browse the{" "}
          <Link to="/" className="tv-link">full index</Link> or{" "}
          <Link to="/submit" className="tv-link">submit a tool</Link>.
        </p>
      )}

      {/* More lists — internal-link rail for SEO equity distribution */}
      {related.length > 0 && (
        <div className="mt-10" data-testid="best-related">
          <div className="text-[11px] uppercase tracking-wide text-[#888] mb-2 border-b border-[#eee] pb-1">
            more lists
          </div>
          <ul className="list-none p-0 m-0 flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
            {related.map((r) => (
              <li key={r.slug}>
                <Link to={`/best/${r.slug}`} className="tv-link" data-testid={`best-related-${r.slug}`}>
                  best {r.label}
                </Link>{" "}
                <span className="text-[#bbb] text-[11px] tabular-nums">{r.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-8 flex flex-wrap gap-2">
        <Link to="/best" className="tv-btn" data-testid="best-all-lists">[all lists]</Link>
        <Link to="/" className="tv-btn" data-testid="best-back-index">[← back to index]</Link>
      </div>
    </Shell>
  );
}

function BestRow({ tool: t }) {
  const price = t.pricing || "$?";
  return (
    <div data-testid={`best-row-${t.slug}`}>
      <div className="flex items-baseline gap-2 flex-wrap">
        <Link to={`/tool/${t.slug}`} className="tv-link font-bold text-[15px]" data-testid={`best-name-${t.slug}`}>
          {t.title}
        </Link>
        {t.description && <span className="text-[12px] text-[#666]">— {t.description}</span>}
      </div>
      <div className="mt-1 flex items-baseline gap-x-2 gap-y-1 flex-wrap text-[12px]">
        {t.asset_class && (
          <Link to={`/c/${slugify(t.asset_class)}`} className="border border-black px-1.5 py-0.5 hover:bg-black hover:text-white no-underline text-black">
            {t.asset_class}
          </Link>
        )}
        {t.category && (
          <Link to={`/f/${slugify(t.category)}`} className="border border-black px-1.5 py-0.5 hover:bg-black hover:text-white no-underline text-black">
            {t.category}
          </Link>
        )}
        <span className="font-bold tabular-nums">{price}</span>
        {typeof t.upvotes === "number" && (
          <span className="text-[#888]">· ▲ {t.upvotes}</span>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <a
          href={outboundHref(t, "best")}
          target="_blank"
          rel="sponsored noopener noreferrer"
          onClick={() => recordLaunch(t.id)}
          className="tv-btn tv-btn-primary"
          data-testid={`best-visit-${t.slug}`}
        >
          [↗] visit {domainOf(t.url)}
        </a>
        <Link to={`/alternatives/${t.slug}`} className="tv-btn" data-testid={`best-alts-${t.slug}`}>
          [alternatives]
        </Link>
      </div>
    </div>
  );
}

function Shell({ testid, children, ...seo }) {
  return (
    <div data-testid={testid}>
      <Seo {...seo} />
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-8">{children}</main>
      <Footer />
    </div>
  );
}
